import React, { useCallback, useEffect, useState } from "react";
import { Lock, LockOpen, Plus, X, Loader2, CalendarOff } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { fetchAdminBlocks, createAdminBlock, deleteAdminBlock, deleteAdminDayBlocks, createAdminBlockRange } from "../../lib/api";

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const fmtIt = (iso) => { const [y, m, d] = iso.split("-"); return `${Number(d)}/${Number(m)}/${y}`; };

// "Chiudi periodo": closes every day in a range for one service.
export function ClosePeriodButton({ service, onDone, testPrefix }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!from || !to) return;
    if (to < from) { toast({ title: "Date non valide", description: "La data di fine deve essere dopo l'inizio." }); return; }
    const days = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
    if (!window.confirm(`Chiudere ${days} giorn${days === 1 ? "o" : "i"} (dal ${fmtIt(from)} al ${fmtIt(to)})? I clienti non potranno prenotare.`)) return;
    setBusy(true);
    try {
      const res = await createAdminBlockRange({ service, from_date: from, to_date: to, reason });
      toast({ title: "Periodo chiuso", description: `${res.days} giorni · ${res.created} nuove chiusure` });
      setOpen(false); setReason("");
      onDone();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid={`${testPrefix}-close-period-toggle`}
        className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm ${open ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "border-[#e8dcc8] hover:bg-white text-[#2b1410]"}`}>
        <CalendarOff className="h-3.5 w-3.5" /> Chiudi periodo
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-[280px] rounded-xl border border-[#e8dcc8] bg-white p-3 shadow-lg space-y-2 text-sm" data-testid={`${testPrefix}-close-period-form`}>
          <div className="text-xs text-[#7c5a4e]">Chiude tutte le giornate nell'intervallo (es. ferie).</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] uppercase text-[#7c5a4e]">Dal
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-0.5 w-full rounded border border-[#e8dcc8] px-2 py-1 text-sm" data-testid={`${testPrefix}-close-period-from`} />
            </label>
            <label className="text-[10px] uppercase text-[#7c5a4e]">Al
              <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-0.5 w-full rounded border border-[#e8dcc8] px-2 py-1 text-sm" data-testid={`${testPrefix}-close-period-to`} />
            </label>
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (solo per te, es. Ferie)"
            className="w-full rounded border border-[#e8dcc8] px-2 py-1 text-sm" data-testid={`${testPrefix}-close-period-reason`} />
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy} data-testid={`${testPrefix}-close-period-submit`}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-neutral-700 hover:bg-neutral-800 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />} Chiudi giorni
            </button>
            <button onClick={() => setOpen(false)} className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Horse ride has a single sunset slot per day: closures are whole days.
export function HorseClosures() {
  const [blocks, setBlocks] = useState([]);
  const [date, setDate] = useState(isoToday());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const from = isoToday();
  const to = (() => { const d = new Date(); d.setDate(d.getDate() + 365); return d.toISOString().slice(0, 10); })();

  const reload = useCallback(async () => {
    try { const res = await fetchAdminBlocks("horse", from, to); setBlocks(res.blocks || []); }
    catch (e) { toast({ title: "Errore chiusure", description: e?.message }); }
  }, [from, to]);
  useEffect(() => { reload(); }, [reload]);

  const addDay = async () => {
    setBusy(true);
    try {
      await createAdminBlock({ service: "horse", date, slot_id: null, reason });
      toast({ title: "Giornata chiusa", description: fmtIt(date) });
      setReason(""); reload();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
    finally { setBusy(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Riaprire la passeggiata del ${fmtIt(b.date)}?`)) return;
    try { await deleteAdminBlock(b.id); reload(); }
    catch (e) { toast({ title: "Errore", description: e?.message }); }
  };

  return (
    <div className="rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4 space-y-3" data-testid="horse-closures">
      <div className="flex items-center gap-2 flex-wrap">
        <Lock className="h-4 w-4 text-[#4a1c17]" />
        <h3 className="font-semibold text-[#2b1410]">Chiusure passeggiata</h3>
        <span className="text-xs text-[#7c5a4e]">Nei giorni chiusi il cliente vede "Nessun turno disponibile".</span>
        <div className="ml-auto"><ClosePeriodButton service="horse" onDone={reload} testPrefix="horse" /></div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[10px] uppercase text-[#7c5a4e]">Giorno
          <input type="date" value={date} min={from} onChange={(e) => setDate(e.target.value)} className="mt-0.5 block rounded border border-[#e8dcc8] bg-white px-2 py-1.5 text-sm" data-testid="horse-close-date" />
        </label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (solo per te)" className="flex-1 min-w-[140px] rounded border border-[#e8dcc8] bg-white px-2 py-1.5 text-sm" data-testid="horse-close-reason" />
        <button onClick={addDay} disabled={busy || !date} data-testid="horse-close-day"
          className="inline-flex items-center gap-1 rounded-full bg-neutral-700 hover:bg-neutral-800 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />} Chiudi giornata
        </button>
      </div>
      {blocks.length === 0 ? (
        <div className="text-xs text-[#7c5a4e]">Nessuna chiusura programmata.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="horse-closures-list">
          {blocks.map((b) => (
            <button key={b.id} onClick={() => remove(b)} title="Clicca per riaprire" data-testid={`horse-closure-${b.date}`}
              className="group inline-flex items-center gap-1 rounded-full bg-neutral-100 hover:bg-neutral-200 border border-neutral-300 text-neutral-700 px-2.5 py-1 text-xs">
              <Lock className="h-3 w-3 group-hover:hidden" /><LockOpen className="h-3 w-3 hidden group-hover:inline" />
              {fmtIt(b.date)}{b.reason ? <span className="opacity-70">· {b.reason}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared closures logic for the Spa / Massage weekly grids.
export function useSlotBlocks(service, fromIso, toIso) {
  const [blocks, setBlocks] = useState([]);
  const reload = useCallback(async () => {
    try {
      const res = await fetchAdminBlocks(service, fromIso, toIso);
      setBlocks(res.blocks || []);
    } catch (e) {
      toast({ title: "Errore chiusure", description: e?.message });
    }
  }, [service, fromIso, toIso]);
  useEffect(() => { reload(); }, [reload]);
  const blockFor = (iso, slotId) =>
    blocks.find((b) => b.date === iso && b.slot_id === null) || blocks.find((b) => b.date === iso && b.slot_id === slotId) || null;
  return { blocks, reload, blockFor };
}

export function FreeSlotCell({ service, iso, slotId, testPrefix, onNewBooking, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const close = async (wholeDay) => {
    setBusy(true);
    try {
      await createAdminBlock({ service, date: iso, slot_id: wholeDay ? null : slotId, reason });
      toast({ title: wholeDay ? "Giornata chiusa" : "Turno chiuso", description: "Non più prenotabile dai clienti." });
      setOpen(false); setReason("");
      onChanged();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 hover:border-emerald-400 text-emerald-800 px-2 py-1.5 text-center text-[11px] transition-colors"
        data-testid={`${testPrefix}-cell-free-${iso}-${slotId}`}
        title="Nuova prenotazione o chiusura"
      >
        libero
      </button>
    );
  }
  return (
    <div className="rounded-md border border-[#e8dcc8] bg-white shadow-md p-1.5 space-y-1 text-[11px] min-w-[120px]" data-testid={`${testPrefix}-cell-menu-${iso}-${slotId}`}>
      <div className="flex items-center justify-between text-[10px] text-[#7c5a4e]">
        <span>Azioni</span>
        <button onClick={() => setOpen(false)} className="p-0.5 hover:text-[#2b1410]" data-testid={`${testPrefix}-cell-menu-close`}><X className="h-3 w-3" /></button>
      </div>
      <button onClick={() => { setOpen(false); onNewBooking(); }} disabled={busy}
        className="w-full inline-flex items-center gap-1 rounded bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 px-2 py-1 font-semibold"
        data-testid={`${testPrefix}-cell-new-${iso}-${slotId}`}>
        <Plus className="h-3 w-3" /> Nuova prenotazione
      </button>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (solo per te)"
        className="w-full rounded border border-[#e8dcc8] px-1.5 py-1 text-[11px]" data-testid={`${testPrefix}-cell-reason`} />
      <button onClick={() => close(false)} disabled={busy}
        className="w-full inline-flex items-center gap-1 rounded bg-neutral-100 hover:bg-neutral-200 border border-neutral-300 text-neutral-800 px-2 py-1 font-semibold disabled:opacity-50"
        data-testid={`${testPrefix}-cell-block-slot-${iso}-${slotId}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />} Chiudi turno
      </button>
      <button onClick={() => close(true)} disabled={busy}
        className="w-full inline-flex items-center gap-1 rounded bg-neutral-700 hover:bg-neutral-800 text-white px-2 py-1 font-semibold disabled:opacity-50"
        data-testid={`${testPrefix}-cell-block-day-${iso}`}>
        <Lock className="h-3 w-3" /> Chiudi giornata
      </button>
    </div>
  );
}

export function BlockedSlotCell({ block, service, iso, slotId, testPrefix, onChanged }) {
  const [busy, setBusy] = useState(false);
  const wholeDay = block.slot_id === null;
  const reopen = async () => {
    const msg = wholeDay ? `Riaprire tutta la giornata del ${iso}?` : "Riaprire questo turno?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      if (wholeDay) await deleteAdminDayBlocks(service, iso); else await deleteAdminBlock(block.id);
      toast({ title: wholeDay ? "Giornata riaperta" : "Turno riaperto" });
      onChanged();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(false); }
  };
  return (
    <button onClick={reopen} disabled={busy}
      className="w-full rounded-md border border-neutral-300 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 px-2 py-1.5 text-center text-[11px] transition-colors group"
      data-testid={`${testPrefix}-cell-blocked-${iso}-${slotId}`}
      title={block.reason ? `Chiuso: ${block.reason}` : "Chiuso · clicca per riaprire"}>
      <span className="inline-flex items-center gap-1 group-hover:hidden">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />} {wholeDay ? "chiuso · giorno" : "chiuso"}
      </span>
      <span className="hidden group-hover:inline-flex items-center gap-1"><LockOpen className="h-3 w-3" /> riapri</span>
      {block.reason && <div className="text-[9px] opacity-70 truncate">{block.reason}</div>}
    </button>
  );
}
